import CustomBreadcrumbs from "../../components/Tools/Breadcumbs";
import Spinner from "../../components/Tools/spinner";
import SubHeader from "../../components/Subheader/Subheader";
import CreateButton from "../../components/Tools/CreateButton";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { toast } from "react-toastify";
import {
  get_draft_sanction_orders,
  delete_draft_sanction_order,
} from "../../helper/Api";

const DraftSanctionOrders = () => {
  const { financialYears, authData } = useSelector((state) => state.authData);
  const fyList = financialYears.payload.fy_list;
  const fyIdRef = useRef(localStorage.getItem("globalDateId"));
  const [fyId, setFyId] = useState(fyIdRef.current);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const handlePageTrigger = () => {
    const nextFyId = localStorage.getItem("globalDateId");
    fyIdRef.current = nextFyId;
    setFyId(nextFyId);
    setPage(1);
  };

  const fetchList = async () => {
    try {
      setLoading(true);
      const res = await get_draft_sanction_orders({
        fy_id: fyIdRef.current,
        page,
        per_page: perPage,
      });
      if (res.data.status) {
        setRows(res.data.list || []);
        setTotalCount(Number(res.data.total_count ?? res.data.count ?? 0));
      } else {
        setRows([]);
        setTotalCount(0);
        toast.error(res.data.message || "Unable to fetch drafts", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      setRows([]);
      setTotalCount(0);
      toast.error(error?.response?.data?.message || "Unable to fetch drafts", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fyId, page, perPage]);

  const openDeleteConfirm = (row) => {
    setRowPendingDelete(row);
    setDeleteConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (deleting) return;
    setDeleteConfirmOpen(false);
    setRowPendingDelete(null);
  };

  const confirmDeleteDraft = async () => {
    if (!rowPendingDelete?.id) return;
    try {
      setDeleting(true);
      const res = await delete_draft_sanction_order(rowPendingDelete.id);
      if (res.data.status) {
        toast.success(res.data.message || "Draft deleted successfully", {
          position: toast.POSITION.TOP_CENTER,
        });
        setDeleteConfirmOpen(false);
        setRowPendingDelete(null);
        fetchList();
      } else {
        toast.error(res.data.message || "Unable to delete draft", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to delete draft", {
        position: toast.POSITION.TOP_CENTER,
      });
    } finally {
      setDeleting(false);
    }
  };

  const columns = [
    {
      name: "S.No",
      width: "70px",
      cell: (_row, index) => (page - 1) * perPage + index + 1,
    },
    {
      name: "Sanction Order No",
      selector: (row) => row.sanction_order_no || "—",
      wrap: true,
    },
    {
      name: "Date",
      selector: (row) => row.sanction_order_date || "—",
      wrap: true,
    },
    {
      name: "Value",
      selector: (row) =>
        row.sanction_order_value != null && row.sanction_order_value !== ""
          ? row.sanction_order_value
          : "—",
      wrap: true,
    },
    {
      name: "Vouchers",
      selector: (row) => row.voucher_count ?? "—",
      width: "100px",
    },
    {
      name: "Invoices",
      selector: (row) => row.invoice_count ?? "—",
      width: "100px",
    },
    {
      name: "Updated On",
      selector: (row) => row.updated_on || row.added_on || "—",
      wrap: true,
    },
    {
      name: "Action",
      width: "120px",
      cell: (row) => (
        <div className="d-flex gap-2">
          <Tooltip title="Continue" arrow>
            <IconButton
              size="small"
              onClick={() => navigate(`/add-sanction-order/${row.id}`)}
              sx={{
                width: 28,
                height: 28,
                border: "1px solid #1976d2",
                color: "#1976d2",
                borderRadius: "6px",
              }}
            >
              <EditOutlinedIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <IconButton
              size="small"
              onClick={() => openDeleteConfirm(row)}
              sx={{
                width: 28,
                height: 28,
                border: "1px solid #d32f2f",
                color: "#d32f2f",
                borderRadius: "6px",
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div>
      {loading && <Spinner />}
      <SubHeader fy_list={fyList} handlePageTrigger={handlePageTrigger} />
      <div className="scheme p-2">
        <div className="row" style={{ padding: "10px 16px" }}>
          <div className="col-md-6">
            <h4 style={{ color: "#28326f", margin: 0 }}>Draft Sanction Orders</h4>
            <CustomBreadcrumbs linksArray={[]} title="Draft Sanction Orders" />
          </div>
          <div className="col-md-6">
            <div className="d-flex gap-2" style={{ width: "auto", float: "right" }}>
              <button
                type="button"
                className="navigation_button"
                onClick={() => navigate("/invoices")}
              >
                <span className="fs-14px">Vendor Payment</span>
              </button>
            </div>
          </div>
        </div>
        <div className="card p-2">
          <DataTable
            columns={columns}
            data={rows}
            pagination
            paginationServer
            paginationTotalRows={totalCount}
            paginationDefaultPage={page}
            paginationPerPage={perPage}
            onChangePage={(p) => setPage(p)}
            onChangeRowsPerPage={(newPerPage, p) => {
              setPerPage(newPerPage);
              setPage(p);
            }}
            highlightOnHover
            dense
            noDataComponent="No draft sanction orders found"
          />
        </div>
        {authData.user.user_type === "l1" && (
          <CreateButton title="Add Sanction Order" url="/add-sanction-order" />
        )}
      </div>

      <Dialog
        open={deleteConfirmOpen}
        onClose={closeDeleteConfirm}
        maxWidth="xs"
        fullWidth
        aria-labelledby="draft-sanction-delete-title"
      >
        <DialogTitle id="draft-sanction-delete-title">
          Delete draft
        </DialogTitle>
        <DialogContent dividers>
          <p className="mb-2" style={{ fontSize: 15, lineHeight: 1.5 }}>
            Are you sure you want to delete this draft sanction order? This
            action cannot be undone.
          </p>
          {rowPendingDelete && (
            <p className="mb-0 text-muted small">
              Sanction Order No.:{" "}
              <strong>{rowPendingDelete.sanction_order_no || "—"}</strong>
            </p>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, pb: 2 }}>
          <Button onClick={closeDeleteConfirm} color="inherit" disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteDraft}
            variant="contained"
            color="error"
            disabled={deleting}
          >
            {deleting ? "Deleting..." : "Yes, delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DraftSanctionOrders;
